// Session 34 · P1 — v2 booking Success page.
/* eslint-disable react/no-unescaped-entities */
// Idempotent: safe to refresh — polls the v2 request state until it advances
// past `pending_payment`. The webhook is the source of truth; this page just
// mirrors it.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 45000; // 45s — webhook typically fires within seconds

function fmtDateTime(d, t) {
  if (!d) return "";
  try {
    return new Date(`${d}T${t || "00:00"}:00`).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return `${d} · ${t}`; }
}

function fmtMoney(cents) {
  if (cents == null) return "";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function BookingV2Success() {
  const [sp] = useSearchParams();
  const reqId = sp.get("req_id");
  const sessionId = sp.get("session_id");
  const [br, setBr] = useState(null);
  const [error, setError] = useState("");
  const [waited, setWaited] = useState(0);

  useEffect(() => {
    if (!reqId) { setError("Missing booking reference."); return; }
    let cancelled = false;
    let elapsed = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const url = `/bookings/v2/${reqId}${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ""}`;
        const r = await api.get(url);
        if (cancelled) return;
        setBr(r.data);
        // Stop polling once we've reached a terminal state
        const terminal = ["confirmed", "pending_request", "refunded", "declined",
                          "expired_unpaid", "payment_failed", "cancelled",
                          "deposit_paid", "balance_processing", "fully_paid"];
        if (terminal.includes(r.data.state)) return;
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load booking.");
        return;
      }
      elapsed += POLL_INTERVAL_MS;
      setWaited(elapsed);
      if (elapsed < POLL_TIMEOUT_MS && !cancelled) {
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [reqId, sessionId]);

  const stillWaiting = !br || ["pending_payment", "pending_lock"].includes(br?.state);
  const timeout = stillWaiting && waited >= POLL_TIMEOUT_MS;

  const content = useMemo(() => {
    if (error) return (
      <div className="m-callout" data-testid="v2-success-error">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 10 }}>We couldn't find your booking.</h2>
        <p>{error}</p>
        <p style={{ marginTop: 12 }}>
          If you completed a payment, please contact <a href="mailto:hello@workmetier.com">hello@workmetier.com</a> with this reference: <code>{reqId}</code>
        </p>
      </div>
    );
    if (timeout && stillWaiting) return (
      <div className="m-callout" data-testid="v2-success-timeout">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 10 }}>Almost there…</h2>
        <p>We're waiting on payment confirmation from Stripe. This usually takes a few seconds.
        Feel free to refresh — your booking will show up as soon as it's confirmed.</p>
        <p className="m-muted small">Reference: <code>{reqId}</code></p>
      </div>
    );
    if (stillWaiting) return (
      <div className="m-callout" data-testid="v2-success-pending">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 10 }}>Confirming your booking…</h2>
        <p>Just a moment while we verify your payment.</p>
      </div>
    );

    const p = br?.payment || {};
    const when = fmtDateTime(br?.date, br?.start_time);

    if (br.state === "confirmed") return (
      <div className="m-callout" data-testid="v2-success-confirmed">
        <div style={{ letterSpacing: 0.16, textTransform: "uppercase", fontSize: 11, color: "rgba(27,35,64,0.55)" }}>Confirmed</div>
        <h2 style={{ fontFamily: "Georgia,serif", fontSize: 32, margin: "8px 0 4px", color: "var(--m-navy)" }}>You're booked.</h2>
        <div style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.85)" }}>
          {br.workspace_name} · {when} · {br.hours}h
        </div>
        <div className="m-muted small" style={{ marginTop: 6 }}>Paid {fmtMoney(p.gross_cents)} · confirmation sent to {br.member_email}</div>
      </div>
    );

    if (br.state === "pending_request") return (
      <div className="m-callout" data-testid="v2-success-pending-request">
        <div style={{ letterSpacing: 0.16, textTransform: "uppercase", fontSize: 11, color: "rgba(27,35,64,0.55)" }}>Awaiting host approval</div>
        <h2 style={{ fontFamily: "Georgia,serif", fontSize: 32, margin: "8px 0 4px", color: "var(--m-navy)" }}>Request submitted.</h2>
        <div style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.85)" }}>
          {br.workspace_name} · {when} · {br.hours}h
        </div>
        <p style={{ fontSize: 14.5, lineHeight: 1.7, color: "rgba(27,35,64,0.8)", marginTop: 12 }}>
          Your payment of <b>{fmtMoney(p.gross_cents)}</b> has been captured and is being held.
          The host has until <b>{new Date(br.request_expires_at).toLocaleString()}</b> to approve or decline.
          If they don't respond in time, or if they decline, your payment will be fully refunded automatically.
        </p>
      </div>
    );

    if (br.state === "refunded") return (
      <div className="m-callout" data-testid="v2-success-refunded">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 6 }}>Refund issued.</h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.7 }}>
          {br.availability_lost_after_capture
            ? "Between the moment you paid and the moment we tried to confirm, this slot became unavailable. Your full payment is being refunded to the original method."
            : "This booking was fully refunded. Please allow 5–10 business days for it to appear on your statement."}
        </p>
        <p className="m-muted small" style={{ marginTop: 10 }}>{when} · {fmtMoney(p.refunded_cents || p.gross_cents)} refunded</p>
      </div>
    );

    if (br.state === "declined") return (
      <div className="m-callout" data-testid="v2-success-declined">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 6 }}>Your request wasn't accepted this time.</h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.7 }}>
          {br.decline_reason ? <><em>"{br.decline_reason}"</em><br/><br/></> : null}
          Your payment will be fully refunded within a few business days.
        </p>
      </div>
    );

    if (br.state === "deposit_paid" || br.state === "balance_processing") {
      const depCents = (br.payment?.deposit?.cents) || 0;
      const balCents = (br.payment?.balance?.cents) || 0;
      const totalCents = p.gross_cents || (depCents + balCents);
      let dueLabel = "your event day";
      try {
        if (br.balance_due_at) {
          dueLabel = new Date(br.balance_due_at).toLocaleString(undefined, {
            month: "long", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
          });
        }
      } catch { /* ignore */ }
      return (
        <div className="m-callout" data-testid="v2-success-deposit-paid">
          <div style={{ letterSpacing: 0.16, textTransform: "uppercase", fontSize: 11, color: "rgba(27,35,64,0.55)" }}>Deposit received</div>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 32, margin: "8px 0 4px", color: "var(--m-navy)" }}>You're booked.</h2>
          <div style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.85)" }}>
            {br.workspace_name} · {when} · {br.hours}h
          </div>
          <div style={{ marginTop: 16, padding: "14px 16px", background: "rgba(184,145,58,0.06)", border: "1px solid rgba(184,145,58,0.28)", borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: "rgba(27,35,64,0.7)" }}>Reservation total</span>
              <b>{fmtMoney(totalCents)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 5 }}>
              <span style={{ color: "rgba(27,35,64,0.7)" }}>Deposit paid today</span>
              <b data-testid="v2-success-deposit-amount">{fmtMoney(depCents)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 5, borderTop: "1px solid rgba(27,35,64,0.08)", paddingTop: 6 }}>
              <span style={{ color: "rgba(27,35,64,0.7)" }}>Remaining balance</span>
              <b data-testid="v2-success-balance-amount">{fmtMoney(balCents)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 5 }}>
              <span style={{ color: "rgba(27,35,64,0.7)" }}>Balance due</span>
              <b>{dueLabel}</b>
            </div>
          </div>
          <p className="m-muted small" style={{ marginTop: 10 }}>
            Your saved payment method will be charged automatically for the remaining balance on the due date.
            You'll receive a confirmation email at {br.member_email}.
          </p>
        </div>
      );
    }

    if (br.state === "fully_paid") {
      const depCents = (br.payment?.deposit?.cents) || 0;
      const balCents = (br.payment?.balance?.cents) || 0;
      const totalCents = p.gross_cents || (depCents + balCents);
      return (
        <div className="m-callout" data-testid="v2-success-fully-paid">
          <div style={{ letterSpacing: 0.16, textTransform: "uppercase", fontSize: 11, color: "rgba(27,35,64,0.55)" }}>Fully paid</div>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 32, margin: "8px 0 4px", color: "var(--m-navy)" }}>Balance charged — see you soon.</h2>
          <div style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(27,35,64,0.85)" }}>
            {br.workspace_name} · {when} · {br.hours}h · Total paid {fmtMoney(totalCents)}
          </div>
        </div>
      );
    }

    if (br.state === "payment_failed") return (
      <div className="m-callout" data-testid="v2-success-payment-failed">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 6 }}>Payment issue — action needed.</h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.7 }}>
          We couldn't collect the remaining balance for your reservation. Your booking is held for 48 hours.
          Please reply to your confirmation email or contact the host to resolve payment — otherwise the reservation will be cancelled automatically.
        </p>
      </div>
    );

    return (
      <div className="m-callout" data-testid="v2-success-other">
        <h2 style={{ fontFamily: "Georgia,serif", marginBottom: 6 }}>Booking status: {br.state}</h2>
        <p>{when}</p>
      </div>
    );
  }, [br, error, stillWaiting, timeout, reqId]);

  return (
    <AppShell>
      <div className="rec-hero" style={{ paddingTop: 40, paddingBottom: 24 }}>
        <div className="rec-container" style={{ maxWidth: 720 }}>
          {content}
          <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/spaces" className="m-btn-ghost" data-testid="v2-success-browse-more">Browse more workspaces</Link>
            <Link to="/" className="m-btn-gold" data-testid="v2-success-home">Return home</Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
